import React from 'react';
import {
  getClassNameFactory,
  Card,
  Heading,
  FlexGrid,
  Typography,
  TextBoldness,
  Link,
} from '@dcl/ui';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { ArticleIcon } from '../../../icons/ArticleIcon';
import { PDFIcon } from '../../../icons/PDFIcon';
import { FileIcon } from '../../../icons/FileIcon';
import { VideoIcon } from '../../../icons/VideoIcon';
import './ArticleTile.css';
import { NewTabIcon } from '../../../icons/NewTabIcon';
import { IArticleTile } from '../types';
import ReactHtmlParser from 'react-html-parser';
import { Badge } from '../../../../Badge';

export const ArticleTile: React.FC<IArticleTile> = (props) => {
  const { articleTile, isCol6 } = props;

  const {
    heading,
    segmentationTitle,
    segmentationText,
    articleFormat,
    articleSize,
    articleReadTime,
    articleDescription,
    articleLinkHref,
    articleLinkText,
    isNewTab,
    badge,
  } = articleTile;
  const getClassName = getClassNameFactory(ArticleTile.displayName);

  const getArticleIcon = () => {
    switch (articleFormat.toLocaleLowerCase()) {
      case 'article':
        return <ArticleIcon />;
      case 'pdf':
        return <PDFIcon />;
      case 'file':
        return <FileIcon />;
      case 'video':
        return <VideoIcon />;
      default:
        return;
    }
  };

  const getArticleSize = articleSize && (
    <Typography
      tag={TypographyTags.SPAN}
      boldness={TextBoldness.Light}
      className={getClassName({ descendantName: 'articleFormat' })}
    >
      {articleSize}
    </Typography>
  );

  const segmentationRow = (
    <div className={getClassName({ descendantName: 'Info' })}>
      <div className={getClassName({ descendantName: 'Icon' })}>
        {getArticleIcon()}
      </div>
      <Typography
        tag={TypographyTags.SPAN}
        boldness={TextBoldness.Light}
        className={getClassName({
          descendantName: 'articleFormat',
        })}
      >
        {articleFormat}
      </Typography>
      {getArticleSize}
      <Typography tag={TypographyTags.SPAN} boldness={TextBoldness.Regular}>
        {articleReadTime}
      </Typography>
    </div>
  );

  const segmentationTitleRow = (
    segmentationTitle: string,
    segmentationText
  ) => {
    return segmentationTitle.toLocaleLowerCase() === 'return type' ? (
      <>
        {' '}
        <FlexGrid.Col xs={4} sm={4} md={4}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Regular}
            className={getClassName({
              descendantName: 'segmentationTitle',
            })}
          >
            {segmentationTitle}:
          </Typography>
        </FlexGrid.Col>
        <FlexGrid.Col xs={8} sm={8} md={8}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Light}
            className={getClassName({
              descendantName: 'segmentationText',
            })}
          >
            {segmentationText}
          </Typography>
        </FlexGrid.Col>
      </>
    ) : (
      <>
        {' '}
        <FlexGrid.Col xs={3} sm={3} md={3}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Regular}
            className={getClassName({
              descendantName: 'segmentationTitle',
            })}
          >
            {segmentationTitle}:
          </Typography>
        </FlexGrid.Col>
        <FlexGrid.Col xs={9} sm={9} md={9}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Light}
            className={getClassName({
              descendantName: 'segmentationText',
            })}
          >
            {segmentationText}
          </Typography>
        </FlexGrid.Col>
      </>
    );
  };

  return (
    <div className={getClassName()}>
      <Card.CardBase size="small" data-testid="ArticleTile">
        <div className={getClassName({ descendantName: 'articleBadge' })}>
          <Badge badge={badge}></Badge>
        </div>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col
              xs={12}
              sm={12}
              md={12}
              className={getClassName({ descendantName: 'articleHeading' })}
            >
              <Heading
                type={types.Medium}
                boldType={boldTypes.medium}
                className={getClassName({
                  descendantName: 'articleTitleHeading',
                })}
              >
                <div title={heading}>{heading}</div>
              </Heading>
            </FlexGrid.Col>
          </FlexGrid.Row>
          {isCol6 ? (
            <FlexGrid.Row
              className={getClassName({ descendantName: 'eyeBrow-mobile' })}
            >
              <FlexGrid.Col lg={6} sm={12} md={6}>
                <FlexGrid.Row>
                  <FlexGrid.Col xs={4} sm={4} md={4}>
                    <Typography
                      tag={TypographyTags.SPAN}
                      boldness={TextBoldness.Regular}
                      className={getClassName({
                        descendantName: 'segmentationTitle',
                      })}
                    >
                      {segmentationTitle}:
                    </Typography>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={8} sm={8} md={8}>
                    <Typography
                      tag={TypographyTags.SPAN}
                      boldness={TextBoldness.Light}
                      className={getClassName({
                        descendantName: 'segmentationText',
                      })}
                    >
                      {segmentationText}
                    </Typography>
                  </FlexGrid.Col>
                </FlexGrid.Row>
              </FlexGrid.Col>

              <FlexGrid.Col
                lg={6}
                sm={12}
                md={6}
                className={getClassName({ descendantName: 'col6Article' })}
              >
                <FlexGrid.Row>
                  <FlexGrid.Col xs={4} sm={4} md={4}>
                    <Typography
                      tag={TypographyTags.SPAN}
                      boldness={TextBoldness.Regular}
                      className={getClassName({
                        descendantName: 'articleFormat',
                      })}
                    >
                      {'Format'}:
                    </Typography>
                  </FlexGrid.Col>

                  <FlexGrid.Col xs={8} sm={8} md={8}>
                    {segmentationRow}
                  </FlexGrid.Col>
                </FlexGrid.Row>
              </FlexGrid.Col>
            </FlexGrid.Row>
          ) : (
            <FlexGrid.Row
              className={getClassName({ descendantName: 'eyeBrow' })}
            >
              {segmentationTitleRow(segmentationTitle, segmentationText)}

              <FlexGrid.Col xs={3} sm={3} md={3}>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Regular}
                  className={getClassName({ descendantName: 'articleFormat' })}
                >
                  {'Format'}:
                </Typography>
              </FlexGrid.Col>

              <FlexGrid.Col xs={9} sm={9} md={9}>
                {segmentationRow}
              </FlexGrid.Col>
            </FlexGrid.Row>
          )}

          <FlexGrid.Row
            className={getClassName({ descendantName: 'description' })}
          >
            <FlexGrid.Col xs={12} sm={12} md={12}>
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Regular}
                className={getClassName({
                  descendantName: 'articleDescription',
                })}
              >
                <div title={articleDescription}>
                  {ReactHtmlParser(articleDescription)}
                </div>
              </Typography>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'link' })}>
          <FlexGrid.Col xs={12} sm={12} md={12}>
            {isNewTab ? (
              <Link
                href={articleLinkHref}
                newWindow={isNewTab}
                iconRight={<NewTabIcon />}
              >
                {articleLinkText}
              </Link>
            ) : (
              <Link href={articleLinkHref} newWindow={isNewTab}>
                {articleLinkText}
              </Link>
            )}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </Card.CardBase>
    </div>
  );
};

ArticleTile.displayName = 'ArticleTile';
